import { TextField } from "@mui/material";
import { Dialog } from "components/Dialog";

import { answerableRows, type BulkResolveRow } from "./bulkResolveWithValue.helpers";
import { BulkAnswerRadios, BulkResultTable, BulkRowsTable } from "./BulkResolveWithValueParts";
import { type ActionKey } from "./DiscrepancyReviewDialog.helpers";
import { type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import { useBulkResolveWithValueForm } from "./useBulkResolveWithValueForm";

export interface BulkResolveWithValueDialogProps {
  open: boolean;
  rows: BulkResolveRow[];
  clearSelection: () => void;
  /** "Non-Discrepancy", pre-computed by the parent against these same rows. Always offered. */
  dismissOption: ResolveActionOption;
  /** "Can't Resolve — Mark as Blocked", pre-computed by the parent. Undefined hides the option. */
  cantDetermineOption?: ResolveActionOption;
  /** Other in-flight row mutations from the parent dialog — Save stays disabled while one runs. */
  otherMutationLoading: boolean;
  onClose: () => void;
  /** Reused for the answers that don't call resolveDiscrepancies: dismiss and "can't determine". */
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  notifyMutated: () => void;
}

/**
 * Bulk mode for the staff resolve-with-value flow: a table of every selected
 * row, one question ("what's correct for all N?"), and a summary before
 * saving. Splits into per-target groups so each resolveDiscrepancies call
 * only asserts eligibility for the rows it actually touches.
 */
export function BulkResolveWithValueDialog(props: BulkResolveWithValueDialogProps) {
  const { open, rows, cantDetermineOption, onClose } = props;
  const form = useBulkResolveWithValueForm(props);

  if (rows.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (form.loading) return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
      title={form.title}
      primaryActionButtonProps={{
        onClick: () => form.handleSave(),
        loading: form.resolving,
        disabled: form.saveDisabled,
      }}
      cancelButtonProps={{ disabled: form.loading }}
    >
      <BulkRowsTable rows={rows} willUpdateById={form.willUpdateById} />

      <BulkAnswerRadios
        rowCount={answerableRows(rows).length}
        answer={form.answer}
        loading={form.loading}
        sourceDisabledReasons={form.sourceDisabledReasons}
        cantDetermineOption={cantDetermineOption}
        optionSummaries={form.optionSummaries}
        onSelectAnswer={form.setAnswer}
      />

      <BulkResultTable
        rows={rows}
        answer={form.answer}
        outcomeCounts={form.outcomeCounts}
        targets={form.copyTargets}
        warnings={form.copyWarnings}
      />

      <TextField
        label="Note (optional)"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        size="small"
        value={form.note}
        onChange={(e) => form.setNote(e.target.value)}
      />
    </Dialog>
  );
}
